import { BrowserModule } from '@angular/platform-browser';
import { RouterModule, Routes } from '@angular/router';
import { APP_INITIALIZER, ErrorHandler, Injectable, NgModule, RendererFactory2 } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { AppversionInterceptorService } from './shared/services/appversion.interceptor.service';
import { CoreModule } from './core/core.module';
import { UserDashboardModule } from './user-dashboard/user-dashboard.module';
import { HotspotTrackingModule } from './hotspot-tracking/hotspot-tracking.module';
import { MonitoringModule } from './monitoring/monitoring.module';
import { RealtimeTrackingModule } from './realtime-tracking/realtime-tracking.module';
import { LogEventsModule } from './log-events/log-events.module';
import { LocalLoadsModule } from './local-loads/local-loads.module';
import { AppComponent } from './app.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/browser';
import { environment } from '../environments/environment';
import { ANALYTICS_CONFIGURATION, APP_VERSION } from './core/constants';
import { LoadHandoverModule } from './load-handover/load-handover.module';
import { ReportsModule } from './reports/reports.module';
import { WebhooksEventsModule } from './webhooks-events/webhooks-events.module';
import { ForcedCheckinModule } from './reports/forced-checkin/forced-checkin.module';
import { OverdueProcessModule } from './overdue-process/overdue-process.module';
import { PodsValidationModule } from './pods-validation/pods-validation.module';
import { LiteLoginModule } from './lite-login/lite-login.module';
import { BluechatModule } from './bluechat/bluechat.module';
import { TnxDashboardModule } from './tnx-dashboard/tnx-dashboard.module';
import { CarrierImportModule } from './carrier-import/carrier-import.module';
import { DfManagementModule } from './df-management/df-management.module';
import { DecarbonizationModule } from './decarbonization/decarbonization.module';
import { AuthClientConfig, AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { AuthenticationConfiguration } from './shared/authentication-configuration.model';
import { AuthenticationConfigurationService } from './shared/services/authentication-configuration.service';
import { AnalyticsConfiguration, AnalyticsService, AnalyticsServiceTypes } from './shared/analytics/analytics.service';
import { GoogleAnalyticsService } from './shared/analytics/google-analytics.service';
import { DummyAnalyticsService } from './shared/analytics/dummy-analytics.service';
import { ConfigurationModule } from './configuration/configuration.module';
import { SharedModule } from './shared/shared.module';
import { TruckJourneyModule } from './load-management/truck-journeys/truck-journey.module';

const routes: Routes = [
  { path: '', redirectTo: 'home', pathMatch: 'full' },
  { path: '**', redirectTo: 'home' }
];

const toastrConfig = {
  closeButton: true,
  progressBar: true,
  positionClass: 'toast-bottom-left',
  timeOut: 10000
};

const translationConfig = {
  loader: {
    provide: TranslateLoader,
    useFactory: HttpLoaderFactory,
    deps: [HttpClient]
  }
};

if (environment.sentryEnabled) {
  Sentry.init({
    dsn: 'https://196b4bcf91be458296b4f360bea9f14a@sentry.io/1261889',
    environment: environment.env,
    release: APP_VERSION
  });
}

export function analyticsServiceFactory(rendererFactory: RendererFactory2) {
  const analyticsConfiguration = ANALYTICS_CONFIGURATION as AnalyticsConfiguration;
  if(analyticsConfiguration.type === AnalyticsServiceTypes.GOOGLE) {
      return new GoogleAnalyticsService(rendererFactory);
    } else if(analyticsConfiguration.type === AnalyticsServiceTypes.NONE) {
      return new DummyAnalyticsService();
    } else {
      return new DummyAnalyticsService();
    }
}

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  handleError(err: any): void {
    if (environment.sentryEnabled) {
      Sentry.captureException(err instanceof Error ? err : new Error(JSON.stringify(err)));
    }
    console.error(err);
  }
}

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    BrowserModule,
    AuthModule.forRoot(),
    RouterModule.forRoot(routes, { anchorScrolling: 'enabled'}),
    BrowserAnimationsModule,
    ToastrModule.forRoot(toastrConfig),
    TranslateModule.forRoot(translationConfig),
    CoreModule,
    HotspotTrackingModule,
    UserDashboardModule,
    MonitoringModule,
    RealtimeTrackingModule,
    ForcedCheckinModule,
    WebhooksEventsModule,
    PodsValidationModule,
    LogEventsModule,
    LocalLoadsModule,
    TruckJourneyModule,
    LoadHandoverModule,
    ReportsModule,
    OverdueProcessModule,
    LiteLoginModule,
    BluechatModule,
    TnxDashboardModule,
    CarrierImportModule,
    DfManagementModule,
    DecarbonizationModule,
    ConfigurationModule,
    SharedModule
  ],
  bootstrap: [AppComponent],
  providers: [
    AuthenticationConfigurationService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuthConfig,
      deps: [AuthenticationConfigurationService, AuthClientConfig],
      multi: true
    },
    { provide: APP_INITIALIZER, useFactory: () => initializeApp, multi: true },
    { provide: ErrorHandler, useClass: SentryErrorHandler },
    { provide: HTTP_INTERCEPTORS, useClass: AppversionInterceptorService, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: AnalyticsService, useFactory: analyticsServiceFactory, deps: [RendererFactory2] },
  []]
})
export class AppModule {
}

// required for AOT compilation
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/messages_', `.json?version=${APP_VERSION}`);
}

export function initializeAuthConfig(authenticationConfigurationService: AuthenticationConfigurationService, authClientConfig: AuthClientConfig): () => Promise<any> {
  return () => authenticationConfigurationService.loadConfiguration()
    .then((config: AuthenticationConfiguration) =>
      authClientConfig.set({
        clientId: config.clientId,
        domain: config.domain.slice(0, -1),
        audience: config.audience,
        redirectUri: window.location.origin,
        httpInterceptor: {
          allowedList: ['/*']
        },
        cacheLocation: 'localstorage'
      })
    );
}

function initializeApp(): Promise<any> {
  return new Promise((resolve, reject) => {
    const existingLocale = localStorage.getItem("locale");
    if(existingLocale) {
      let transformedLocale = existingLocale;
      switch(existingLocale) {
        case "ae": transformedLocale = "ar_ae"; break;
        case "sa": transformedLocale = "ar_sa"; break;
        case "za": transformedLocale = "af_za"; break;
        case "bg": transformedLocale = "bg_bg"; break;
        case "cz": transformedLocale = "cs_cz"; break;
        case "dk": transformedLocale = "da_dk"; break;
        case "at": transformedLocale = "de_at"; break;
        case "de": transformedLocale = "de_de"; break;
        case "au": transformedLocale = "en_au"; break;
        case "ca": transformedLocale = "en_ca"; break;
        case "gb": transformedLocale = "en_gb"; break;
        case "en": transformedLocale = "en_us"; break;
        case "gr": transformedLocale = "el_gr"; break;
        case "es": transformedLocale = "es_es"; break;
        case "mx": transformedLocale = "_es_419"; break;
        case "fi": transformedLocale = "fi_fi"; break;
        case "be": transformedLocale = "fr_be"; break;
        case "ca": transformedLocale = "fr_ca"; break;
        case "fr": transformedLocale = "fr_fr"; break;
        case "hu": transformedLocale = "hu_hu"; break;
        case "it": transformedLocale = "it_it"; break;
        case "lt": transformedLocale = "lt_lt"; break;
        case "nl": transformedLocale = "nl_nl"; break;
        case "pl": transformedLocale = "pl_pl"; break;
        case "br": transformedLocale = "pt_br"; break;
        case "pt": transformedLocale = "pt_pt"; break;
        case "ro": transformedLocale = "ro_ro"; break;
        case "ru": transformedLocale = "ru_ru"; break;
        case "sk": transformedLocale = "sk_sk"; break;
        case "sv": transformedLocale = "sv_se"; break;
        case "tr": transformedLocale = "tr_tr"; break;
      }
      if(transformedLocale !== existingLocale) {
        localStorage.setItem("locale", transformedLocale)
      }
    }
    resolve(null);
  })
}
